/**
 * Bade grids ka Excel export — chunked + worker-backed.
 *
 * Flow:
 *   main thread                          worker
 *   ───────────                          ──────
 *   init(meta, totalRows)      ───────>  writer.start()
 *                              <───────  need
 *   rows(chunk 1)              ───────>  writer.writeRows()
 *                              <───────  progress + need
 *   ... (repeat)
 *   finish()                   ───────>  writer.finish()
 *                              <───────  done(blob)
 *
 * Har chunk se pehle main thread worker ke "need" ka intezaar karta hai, isliye
 * na queue badhti hai na memory. Chunk banane ke beech event loop free rehta
 * hai, to UI aur progress bar chalte rehte hain.
 */

import { saveAs } from "file-saver";
import type {
  ExportRequest,
  ExportResponse,
} from "../workers/excelExport.worker";
import {
  XlsxStreamWriter,
  parseCellValue,
  type CellValue,
  type HAlign,
  type SheetMeta,
  type TitleLine,
} from "./xlsxStreamWriter";

/** Ek baar mein itni rows serialize + transfer hoti hain. */
const CHUNK_SIZE = 4000;

/** Auto-width ke liye itni rows hi sample karte hain (poora scan = freeze). */
const WIDTH_SAMPLE_LIMIT = 2000;

// Purane ExcelJS export wale hi constants — width output same rakhne ke liye.
const CHAR_WIDTH = 10;
const MAX_PIXEL_WIDTH = 300;
const EXCEL_WIDTH_FACTOR = 0.15;
const MIN_COL_WIDTH = 12;
const MAX_COL_WIDTH = 55;

export type ExportStage = "preparing" | "writing" | "compressing" | "saving";

export interface ExportProgress {
  stage: ExportStage;
  written: number;
  total: number;
  /** 0-100 */
  percent: number;
}

export interface GridExportColumn {
  header: string;
  field: string;
}

export interface GridExportOptions {
  fileName: string;
  sheetName?: string;
  columns: GridExportColumn[];
  rows: any[];
  pinnedBottomRows?: any[];
  titleLines?: TitleLine[];
  leftHeaders?: string[];
  rightHeaders?: string[];
  headerData?: string[];
  footerData?: string[];
  /** Server ka RptColWidthInfo (character count). */
  colWidthInfo?: number[];
  colAlignInfo?: (HAlign | undefined)[];
  freezeHeader?: boolean;
  autoFilter?: boolean;
  onProgress?: (p: ExportProgress) => void;
  signal?: AbortSignal;
}

const sanitizeFileName = (name: string): string => {
  const cleaned = (name || "Report")
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim();
  return (cleaned || "Report").slice(0, 120);
};

/**
 * Column widths. Server ne width bheji hai to wahi, warna header + sampled rows
 * se estimate. Sample isliye ki 2.5 lakh × 40 ka poora scan hi apne aap mein
 * ek crore String() calls hai.
 */
const computeColWidths = (
  columns: GridExportColumn[],
  rows: any[],
  pinnedRows: any[],
  colWidthInfo: number[]
): number[] => {
  const sampleStep = Math.max(1, Math.ceil(rows.length / WIDTH_SAMPLE_LIMIT));

  return columns.map((col, index) => {
    const info = colWidthInfo[index];
    if (info !== undefined && info !== null) {
      const pixelWidth = Math.min(info * CHAR_WIDTH, MAX_PIXEL_WIDTH);
      return Math.max(
        Math.round(pixelWidth * EXCEL_WIDTH_FACTOR),
        MIN_COL_WIDTH
      );
    }

    const field = col.field;
    let maxLen = col.header?.length || 10;

    for (let i = 0; i < rows.length; i += sampleStep) {
      const v = rows[i]?.[field];
      if (v !== undefined && v !== null) {
        const len = String(v).length;
        if (len > maxLen) maxLen = len;
      }
    }
    for (let i = 0; i < pinnedRows.length; i++) {
      const v = pinnedRows[i]?.[field];
      if (v !== undefined && v !== null) {
        const len = String(v).length;
        if (len > maxLen) maxLen = len;
      }
    }

    return Math.min(
      Math.max(Math.ceil(maxLen * 1.3), MIN_COL_WIDTH),
      MAX_COL_WIDTH
    );
  });
};

/** Row objects → cell arrays, sirf export columns ke liye. */
const serializeChunk = (
  rows: any[],
  start: number,
  end: number,
  fields: string[]
): CellValue[][] => {
  const nCols = fields.length;
  const out: CellValue[][] = new Array(end - start);

  for (let i = start; i < end; i++) {
    const src = rows[i];
    const cells: CellValue[] = new Array(nCols);
    for (let c = 0; c < nCols; c++) {
      cells[c] = src ? parseCellValue(src[fields[c]]) : "";
    }
    out[i - start] = cells;
  }
  return out;
};

const createWorker = (): Worker | null => {
  if (typeof Worker === "undefined") return null;
  try {
    return new Worker(
      new URL("../workers/excelExport.worker.ts", import.meta.url),
      { type: "module" }
    );
  } catch (err) {
    console.warn("Excel export worker start nahi hua, main thread par chalega", err);
    return null;
  }
};

const yieldToMain = () => new Promise<void>((r) => setTimeout(r, 0));

/**
 * Grid ko streaming XLSX mein export karke download kara deta hai.
 * Cancel ke liye `signal` bhejein — AbortError throw hoga.
 */
export async function exportGridToExcel(
  options: GridExportOptions
): Promise<void> {
  const {
    fileName,
    sheetName,
    columns,
    rows,
    pinnedBottomRows = [],
    titleLines = [],
    leftHeaders = [],
    rightHeaders = [],
    headerData = [],
    footerData = [],
    colWidthInfo = [],
    colAlignInfo = [],
    freezeHeader = true,
    autoFilter = true,
    onProgress,
    signal,
  } = options;

  if (!columns.length) throw new Error("Export ke liye koi column nahi mila");

  const fields = columns.map((c) => c.field);
  const totalRows = rows.length + pinnedBottomRows.length;

  const report = (stage: ExportStage, written: number) => {
    onProgress?.({
      stage,
      written,
      total: totalRows,
      percent: totalRows ? Math.min(100, Math.round((written / totalRows) * 100)) : 100,
    });
  };

  report("preparing", 0);
  await yieldToMain();

  const meta: SheetMeta = {
    sheetName: sheetName || "Report",
    headers: columns.map((c) => c.header),
    colWidths: computeColWidths(columns, rows, pinnedBottomRows, colWidthInfo),
    colAligns: columns.map((_, i) => colAlignInfo[i]),
    titleLines,
    leftHeaders,
    rightHeaders,
    headerData,
    footerData,
    freezeHeader,
    autoFilter,
  };

  const worker = createWorker();
  const blob = await (worker
    ? runInWorker(worker, meta, rows, pinnedBottomRows, fields, totalRows, report, signal)
    : runInline(meta, rows, pinnedBottomRows, fields, report, signal));

  report("saving", totalRows);
  saveAs(blob, `${sanitizeFileName(fileName)}.xlsx`);
}

// ── Worker path ─────────────────────────────────────────────────────────────

function runInWorker(
  worker: Worker,
  meta: SheetMeta,
  rows: any[],
  pinnedRows: any[],
  fields: string[],
  totalRows: number,
  report: (stage: ExportStage, written: number) => void,
  signal?: AbortSignal
): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    let cursor = 0;
    let pinnedSent = false;
    let settled = false;

    const cleanup = () => {
      signal?.removeEventListener("abort", onAbort);
      worker.terminate();
    };

    const finishWith = (fn: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      fn();
    };

    function onAbort() {
      finishWith(() => reject(new DOMException("Export cancelled", "AbortError")));
    }
    signal?.addEventListener("abort", onAbort, { once: true });

    const send = (msg: ExportRequest) => worker.postMessage(msg);

    /** Worker ne "need" bheja — agla chunk taiyaar karke bhejo. */
    const sendNext = () => {
      if (settled) return;

      if (cursor < rows.length) {
        const end = Math.min(cursor + CHUNK_SIZE, rows.length);
        const chunk = serializeChunk(rows, cursor, end, fields);
        cursor = end;
        send({ type: "rows", rows: chunk, kind: "data" });
        return;
      }

      if (!pinnedSent) {
        pinnedSent = true;
        if (pinnedRows.length) {
          send({
            type: "rows",
            rows: serializeChunk(pinnedRows, 0, pinnedRows.length, fields),
            kind: "pinned",
          });
          return;
        }
      }

      report("compressing", totalRows);
      send({ type: "finish" });
    };

    worker.onmessage = (e: MessageEvent<ExportResponse>) => {
      const msg = e.data;
      switch (msg.type) {
        case "need":
          sendNext();
          break;
        case "progress":
          report("writing", msg.written);
          break;
        case "done":
          finishWith(() => resolve(msg.blob));
          break;
        case "error":
          finishWith(() => reject(new Error(msg.message)));
          break;
      }
    };

    worker.onerror = (e) => {
      finishWith(() =>
        reject(new Error(e.message || "Excel export worker fail ho gaya"))
      );
    };

    send({ type: "init", meta, totalRows });
  });
}

// ── Fallback: worker na ho to main thread par, par phir bhi chunked ─────────

async function runInline(
  meta: SheetMeta,
  rows: any[],
  pinnedRows: any[],
  fields: string[],
  report: (stage: ExportStage, written: number) => void,
  signal?: AbortSignal
): Promise<Blob> {
  const writer = new XlsxStreamWriter(meta);
  writer.start();

  const throwIfAborted = () => {
    if (signal?.aborted) {
      writer.abort();
      throw new DOMException("Export cancelled", "AbortError");
    }
  };

  for (let start = 0; start < rows.length; start += CHUNK_SIZE) {
    throwIfAborted();
    const end = Math.min(start + CHUNK_SIZE, rows.length);
    writer.writeRows(serializeChunk(rows, start, end, fields));
    report("writing", writer.rowsWritten);
    await yieldToMain();
  }

  throwIfAborted();
  if (pinnedRows.length) {
    writer.writeRows(
      serializeChunk(pinnedRows, 0, pinnedRows.length, fields),
      true
    );
    report("writing", writer.rowsWritten);
    await yieldToMain();
  }

  report("compressing", writer.rowsWritten);
  return writer.finish();
}
